'use client';
import { trackEngagement } from '@/lib/analytics';
import siteConfig from '@/config/siteConfig';

// Definition/Answer Box Component
export function FeaturedSnippet({ 
  question, 
  answer, 
  className = '',
  trackingEnabled = true 
}: {
  question: string;
  answer: string;
  className?: string;
  trackingEnabled?: boolean;
}) {
  const handleClick = () => {
    if (trackingEnabled) {
      trackEngagement('featured_snippet_view', 1);
      
      // Track specific question for content optimization
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'featured_snippet_interaction', {
          event_category: 'Content Optimization',
          event_label: question,
          custom_parameter_snippet_type: 'definition'
        });
      }
    }
  };

  return (
    <div 
      className={`bg-blue-50 border-l-4 border-[#00A4BD] p-6 my-6 ${className}`}
      onClick={handleClick}
    >
      <h3 className="text-lg font-semibold mb-3 text-gray-900">{question}</h3>
      <p className="text-gray-700">{answer}</p>
      
      {/* Organization attribution */}
      <div className="mt-4 pt-3 border-t border-blue-100">
        <div className="flex items-center text-sm text-gray-600">
          <span>Source: {siteConfig.organization.name}</span>
        </div>
      </div>
    </div>
  );
}

// List Component for Step-by-Step Guides
export function FeaturedSnippetList({ 
  question, 
  items, 
  ordered = false,
  className = '',
  trackingEnabled = true
}: {
  question: string;
  items: Array<{ title: string; description?: string }>;
  ordered?: boolean;
  className?: string;
  trackingEnabled?: boolean;
}) {
  const ListComponent = ordered ? 'ol' : 'ul';
  
  const handleClick = () => {
    if (trackingEnabled) {
      trackEngagement('featured_snippet_list_view', items.length);
      
      // Track specific question for content optimization
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'featured_snippet_interaction', {
          event_category: 'Content Optimization',
          event_label: question,
          custom_parameter_snippet_type: ordered ? 'ordered_list' : 'unordered_list',
          custom_parameter_item_count: items.length
        });
      }
    }
  };
  
  return (
    <div className={`bg-blue-50 border-l-4 border-[#00A4BD] p-6 my-6 ${className}`} onClick={handleClick}>
      <h3 className="text-lg font-semibold mb-3 text-gray-900">{question}</h3>
      <ListComponent className={`space-y-2 ${ordered ? 'list-decimal' : 'list-disc'} list-inside`}>
        {items.map((item, index) => (
          <li key={index} className="text-gray-700">
            <span className="font-medium">{item.title}</span>
            {item.description && <span className="text-gray-600"> - {item.description}</span>}
          </li>
        ))}
      </ListComponent>
      
      {/* Organization attribution */}
      <div className="mt-4 pt-3 border-t border-blue-100">
        <div className="flex items-center text-sm text-gray-600">
          <span>Source: {siteConfig.organization.name}</span>
        </div>
      </div>
    </div>
  );
}

// Table Component for Comparisons
export function FeaturedSnippetTable({ 
  question, 
  headers, 
  rows,
  className = '',
  trackingEnabled = true
}: {
  question: string;
  headers: string[];
  rows: Array<{ [key: string]: string }>;
  className?: string;
  trackingEnabled?: boolean;
}) {
  const handleClick = () => {
    if (trackingEnabled) {
      trackEngagement('featured_snippet_table_view', rows.length);
      
      // Track specific question for content optimization
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'featured_snippet_interaction', {
          event_category: 'Content Optimization',
          event_label: question,
          custom_parameter_snippet_type: 'table',
          custom_parameter_row_count: rows.length,
          custom_parameter_column_count: headers.length
        });
      }
    }
  };

  return (
    <div className={`bg-blue-50 border-l-4 border-[#00A4BD] p-6 my-6 ${className}`} onClick={handleClick}>
      <h3 className="text-lg font-semibold mb-3 text-gray-900">{question}</h3>
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white rounded-lg shadow-sm">
          <thead className="bg-gray-50">
            <tr>
              {headers.map((header, index) => (
                <th key={index} className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex} className="border-b border-gray-100">
                {headers.map((header, cellIndex) => (
                  <td key={cellIndex} className="px-4 py-3 text-sm text-gray-700">
                    {row[header] || ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Organization attribution */}
      <div className="mt-4 pt-3 border-t border-blue-100">
        <div className="flex items-center text-sm text-gray-600">
          <span>Source: {siteConfig.organization.name}</span>
        </div>
      </div>
    </div>
  );
}

// FAQ Component optimized for rich snippets
export function FeaturedSnippetFAQ({ 
  faqs,
  className = '',
  trackingEnabled = true
}: {
  faqs: Array<{ question: string; answer: string }>;
  className?: string;
  trackingEnabled?: boolean;
}) {
  const handleClick = (question: string) => {
    if (trackingEnabled) {
      trackEngagement('featured_snippet_faq_click', 1);
      
      // Track specific FAQ for content optimization
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'featured_snippet_interaction', {
          event_category: 'Content Optimization',
          event_label: question,
          custom_parameter_snippet_type: 'faq'
        });
      }
    }
  };

  return (
    <div className={`bg-blue-50 border-l-4 border-[#00A4BD] p-6 my-6 ${className}`}>
      <h3 className="text-lg font-semibold mb-4 text-gray-900">Frequently Asked Questions</h3>
      <div className="space-y-4">
        {faqs.map((faq, index) => (
          <div key={index} className="bg-white rounded-lg p-4" onClick={() => handleClick(faq.question)}>
            <h4 className="font-medium text-gray-900 mb-2">{faq.question}</h4>
            <p className="text-gray-700">{faq.answer}</p>
          </div>
        ))}
      </div>
      
      {/* Organization attribution */}
      <div className="mt-4 pt-3 border-t border-blue-100">
        <div className="flex items-center text-sm text-gray-600">
          <span>Source: {siteConfig.organization.name}</span>
        </div>
      </div>
    </div>
  );
}